import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FinalCTA } from '../components/FinalCTA';
import { ArrowUpRight } from 'lucide-react';
import { useProjects, ProjectCategory } from '../hooks/useProjects';
const categories = ['Todos', 'Oficinas corporativas', 'Sucursales bancarias', 'Retail y tiendas', 'Educación / institucional', 'Industrial / especial', 'Otros'];
export function ProjectsPage() {
  const {
    projects
  } = useProjects();
  const [activeFilter, setActiveFilter] = useState('Todos');
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredProjects = activeFilter === 'Todos' ? projects : projects.filter(project => project.category === activeFilter);
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-blue-600 selection:text-white" data-id="element-434">
      <Header data-id="element-435" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-436">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-gray-50 to-white opacity-70" data-id="element-437"></div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-438">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-439">
            <span className="mb-4 inline-block rounded-full bg-blue-100 px-4 py-1.5 text-sm font-bold text-blue-600" data-id="element-440">
              PORTAFOLIO
            </span>
            <h1 className="mb-6 text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl md:text-7xl" data-id="element-441">
              Nuestros Proyectos
            </h1>
            <p className="mx-auto max-w-3xl text-xl text-gray-600 leading-relaxed mb-4" data-id="element-442">
              Más de 150 proyectos ejecutados, sobre 75.000 metros cuadrados
              entregados y sobre 1.000.000 de UF en contratos administrados para
              más de 30 clientes... y contando.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="pb-12" data-id="element-443">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-444">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4" data-id="element-445">
            {categories.map(category => <button key={category} onClick={() => setActiveFilter(category)} className={`relative px-4 py-2 sm:px-6 sm:py-3 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${activeFilter === category ? 'text-white shadow-lg shadow-blue-500/30' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}`} data-id="element-446">
                {activeFilter === category && <motion.div layoutId="activeTab" className="absolute inset-0 bg-blue-600 rounded-full" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-447" />}
                <span className="relative z-10" data-id="element-448">{category}</span>
              </button>)}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-24 bg-white" data-id="element-449">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-450">
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-451">
            <AnimatePresence mode="popLayout" data-id="element-452">
              {filteredProjects.map(project => <motion.div layout key={project.id} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.4
            }} className="group relative flex flex-col h-full bg-white rounded-[2rem] overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-blue-900/10 transition-all duration-300 border border-gray-100" data-id="element-453">
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden" data-id="element-454">
                    <img src={project.image} alt={project.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-455" />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-456" />

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4" data-id="element-457">
                      <span className="inline-block rounded-full bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold text-blue-900 shadow-sm" data-id="element-458">
                        {project.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-grow p-6" data-id="element-459">
                    <div className="flex justify-between items-start mb-2" data-id="element-460">
                      <h3 className="text-xl font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors" data-id="element-461">
                        {project.name}
                      </h3>
                      <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors shrink-0 ml-2" data-id="element-462" />
                    </div>
                    <p className="text-gray-500 text-sm mb-4 flex-grow" data-id="element-463">
                      {project.description}
                    </p>
                    <div className="pt-4 border-t border-gray-100 flex justify-between items-center text-xs sm:text-sm" data-id="element-464">
                      <div className="flex gap-3 text-gray-400 font-semibold uppercase tracking-wider" data-id="element-465">
                        <span data-id="element-466">{project.year}</span>
                        <span data-id="element-467">•</span>
                        <span data-id="element-468">{project.m2} m²</span>
                        <span data-id="element-469">•</span>
                        <span data-id="element-470">{project.comuna}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </motion.div>

          {filteredProjects.length === 0 && <div className="text-center py-20" data-id="element-471">
              <p className="text-xl text-gray-500" data-id="element-472">
                No se encontraron proyectos en esta categoría.
              </p>
            </div>}
        </div>
      </section>

      <FinalCTA data-id="element-473" />
      <Footer data-id="element-474" />
    </div>;
}